import { supabase, DbVehicle } from './supabase';
import { Vehicle } from '../data/vehicles';

function inferBrand(name: string): string {
  const brands = ['BMW','Chevrolet','Dodge','Ford','GMC','Honda','Hyundai','Infiniti','Jeep','Kia',
    'Land Rover','Mazda','Mercedes-Benz','Mitsubishi','Nissan','Ram','Tesla','Toyota','Audi','Buick',
    'Cadillac','Chrysler','Lincoln','Lexus','Acura','Subaru','Volkswagen','Volvo','Porsche'];
  const upper = name.toUpperCase();
  for (const b of brands) { if (upper.includes(b.toUpperCase())) return b; }
  return name.split(' ')[0] ?? 'Unknown';
}

function inferType(make: string, model: string, bodyStyle: string | null): string {
  const combined = `${make} ${model} ${bodyStyle ?? ''}`.toUpperCase();
  if (/PICKUP|TRUCK|F-?150|F-?250|SILVERADO|SIERRA|RANGER|TACOMA|TUNDRA|RAM\s*\d/.test(combined)) return 'Truck';
  if (/VAN|PROMASTER|SPRINTER|TRANSIT|ODYSSEY|SIENNA|CARAVAN/.test(combined)) return 'Van';
  if (/CONVERTIBLE|CABRIOLET/.test(combined)) return 'Convertible';
  if (/COUPE|CAMARO|MUSTANG|CHALLENGER|CHARGER|CORVETTE/.test(combined)) return 'Coupe';
  if (/SUV|SPORT UTILITY|CX-|EXPLORER|EQUINOX|TERRAIN|TUCSON|SPORTAGE|KICKS|KONA|ESCAPE|PILOT|CR-V|RAV4|HIGHLANDER|TAHOE|SUBURBAN|EXPEDITION|NAVIGATOR|X5|X3|GLE|GLC|Q5|Q7/.test(combined)) return 'SUV';
  return 'Sedan';
}

function inferHorsepower(name: string, fuel: string): number {
  if (fuel === 'Electric') return 300;
  const upper = name.toUpperCase();
  if (/V8|HEMI/.test(upper)) return 370;
  if (/V6/.test(upper)) return 280;
  return 200;
}

export function dbVehicleToVehicle(db: DbVehicle, index: number): Vehicle {
  const make = db.make ?? '';
  const model = db.model ?? '';
  const name = `${make} ${model}${db.trim ? ' ' + db.trim : ''}`.trim();
  const fuel = db.fuel_type ?? 'Gasoline';
  const images = db.images ?? [];
  const image = images[0] ?? '';
  const color = [db.exterior_color, db.interior_color].filter(Boolean).join(' / ') || '—';

  return {
    id: index + 1,
    vin: db.vin ?? '',
    name,
    year: db.year ?? new Date().getFullYear(),
    price: db.price ?? 0,
    image,
    images,
    type: inferType(make, model, db.body_style),
    mileage: db.mileage ?? 0,
    transmission: db.transmission ?? 'Automatic',
    fuel,
    brand: make || inferBrand(name),
    model,
    color,
    engine: db.engine ?? '2.0L I4',
    horsepower: inferHorsepower(name, fuel),
    condition: db.status === 'sold' ? 'Pre-Owned' : (db.status === 'new' ? 'New' : 'Pre-Owned'),
    features: db.features ?? ['Rear Camera', 'Bluetooth', 'Cruise Control', 'Power Windows'],
    description: db.description ?? `${db.year} ${name}. Visit us for more details. Financing available!`,
    sold: db.status === 'sold',
  };
}

export async function fetchAllVehicles(): Promise<Vehicle[]> {
  const { data, error } = await supabase
    .from('vehicles')
    .select('*')
    .order('created_at', { ascending: false });

  if (error) throw error;
  return (data as DbVehicle[]).map((v, i) => dbVehicleToVehicle(v, i));
}

export async function upsertVehicles(vehicles: Vehicle[]): Promise<void> {
  const rows = vehicles.map((v) => {
    const colorParts = v.color?.split(' / ') ?? [];
    return {
      vin: v.vin,
      year: v.year,
      make: v.brand,
      model: v.model ?? '',
      trim: '',
      body_style: v.type,
      exterior_color: colorParts[0] ?? null,
      interior_color: colorParts[1] ?? null,
      mileage: v.mileage,
      price: v.price,
      engine: v.engine,
      transmission: v.transmission,
      drivetrain: null,
      fuel_type: v.fuel,
      status: v.sold ? 'sold' : (v.condition === 'New' ? 'new' : 'available'),
      description: v.description,
      images: v.images ?? [v.image],
      features: v.features,
    };
  });

  const { error } = await supabase
    .from('vehicles')
    .upsert(rows, { onConflict: 'vin', ignoreDuplicates: false });

  if (error) throw error;
}
